import { useState, useEffect } from 'react';
import PolicyViolationsService from '../../../../../../services/PolicyViolations';
import OneOnOneService from '../../../../../../services/PerformanceOneOnOne';
import { INITIAL_FORM_DATA } from './formConstants';
import { UserWithEmployee } from '../../UserSelectionModal';

interface FormData {
  name: string;
  dateOfIncident: string;
  position: string;
  department: string;
  supervisor: string;
  dateOfHire: string;
  policyViolationIds: string[];
  descriptionOfOneOnOne: string;
  coreValues: string[];
  meetingGoal: string;
  privateNotes: string;
}

interface FormErrors {
  name?: string;
  dateOfIncident?: string;
  policyViolationIds?: string;
  descriptionOfOneOnOne?: string;
  coreValues?: string;
  meetingGoal?: string;
}

interface PolicyViolation {
  id: string;
  name: string;
}

interface PriorDiscussion {
  date_of_incident: string;
  type: string;
  policy_violated: string;
}

export const useOneOnOneForm = (
  isOpen: boolean,
  token: string,
  currentUser?: UserWithEmployee,
  submittedForms?: Map<string, FormData>,
  editMode: boolean = false,
  oneOnOneId?: string,
) => {
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<FormErrors>({});
  const [policyViolations, setPolicyViolations] = useState<PolicyViolation[]>([]);
  const [priorDiscussions, setPriorDiscussions] = useState<PriorDiscussion[]>([]);

  const loadPolicyViolations = async () => {
    try {
      const service = PolicyViolationsService(token);
      const response = await service.list();
      setPolicyViolations(response.data || []);
    } catch (error) {
      console.error('Error loading policy violations:', error);
      setPolicyViolations([]);
    }
  };

  const loadPriorDiscussions = async (userId: string) => {
    try {
      const service = OneOnOneService(token);
      const response = await service.getPriorDiscussions(userId);
      setPriorDiscussions(response.data || []);
    } catch (error) {
      console.error('Error loading prior discussions:', error);
      setPriorDiscussions([]);
    }
  };

  const loadOneOnOneData = async (id: string) => {
    try {
      const service = OneOnOneService(token);
      const response = await service.show(id);
      const oneOnOne = response.data;

      if (oneOnOne) {
        let policyViolationIds: string[] = [];

        if (oneOnOne.policyViolationIds && Array.isArray(oneOnOne.policyViolationIds)) {
          policyViolationIds = oneOnOne.policyViolationIds;
        } else if (oneOnOne.policyViolations && Array.isArray(oneOnOne.policyViolations)) {
          policyViolationIds = oneOnOne.policyViolations.map((pv: any) => pv.id || pv);
        } else if (oneOnOne.policyViolationId) {
          policyViolationIds = [oneOnOne.policyViolationId];
        } else if (oneOnOne.policyViolation?.id) {
          policyViolationIds = [oneOnOne.policyViolation.id];
        }

        setFormData({
          name: oneOnOne.name || '',
          dateOfIncident: oneOnOne.dateOfIncident ? new Date(oneOnOne.dateOfIncident).toISOString().split('T')[0] : '',
          position: oneOnOne.position || '',
          department: oneOnOne.department || '',
          supervisor: oneOnOne.supervisor || '',
          dateOfHire: oneOnOne.dateOfHire ? new Date(oneOnOne.dateOfHire).toISOString().split('T')[0] : '2024-01-01',
          policyViolationIds,
          descriptionOfOneOnOne: oneOnOne.descriptionOfOneOnOne || '',
          coreValues: oneOnOne.coreValues || [],
          meetingGoal: oneOnOne.meetingGoal || '',
          privateNotes: oneOnOne.privateNotes || '',
        });

        if (oneOnOne.userId) {
          loadPriorDiscussions(oneOnOne.userId);
        }
      }
    } catch (error) {
      console.error('Error loading one on one data:', error);
    }
  };

  useEffect(() => {
    if (isOpen && token) {
      loadPolicyViolations();
    }
  }, [isOpen, token]);

  useEffect(() => {
    if (isOpen && editMode && oneOnOneId && token) {
      loadOneOnOneData(oneOnOneId);
    }
  }, [isOpen, editMode, oneOnOneId, token]);

  useEffect(() => {
    if (isOpen && !editMode && currentUser) {
      const savedForm = submittedForms?.get(currentUser.id);
      if (savedForm) {
        setFormData(savedForm);
      } else {
        setFormData({
          name: `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim(),
          dateOfIncident: '',
          position: currentUser.employee?.title?.name || '',
          department: currentUser.employee?.department?.name || '',
          supervisor: currentUser.employee?.supervisor?.name || '',
          dateOfHire: currentUser.employee?.startDate || '2024-01-01',
          policyViolationIds: [],
          descriptionOfOneOnOne: '',
          coreValues: [],
          meetingGoal: '',
          privateNotes: '',
        });
      }
      setErrors({});
      loadPriorDiscussions(currentUser.id);
    }
  }, [isOpen, editMode, currentUser]);

  useEffect(() => {
    if (!isOpen) {
      setFormData(INITIAL_FORM_DATA);
      setErrors({});
      setPriorDiscussions([]);
    }
  }, [isOpen]);

  const handleInputChange = (field: keyof FormData, value: string | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    const today = new Date().toISOString().split('T')[0];

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.dateOfIncident) {
      newErrors.dateOfIncident = 'Date of Incident is required';
    } else if (formData.dateOfIncident > today) {
      newErrors.dateOfIncident = 'Date of Incident cannot be in the future';
    }
    if (formData.policyViolationIds.length === 0) {
      newErrors.policyViolationIds = 'At least one Policy Violation is required';
    }
    if (!formData.descriptionOfOneOnOne.trim()) {
      newErrors.descriptionOfOneOnOne = 'Description of One On One is required';
    }
    if (formData.coreValues.length === 0) {
      newErrors.coreValues = 'At least one core value must be selected';
    }
    if (!formData.meetingGoal.trim()) {
      newErrors.meetingGoal = 'Meeting Goal is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return {
    formData,
    errors,
    policyViolations,
    priorDiscussions,
    handleInputChange,
    validateForm,
    setFormData,
  };
};
